import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { sendWhatsApp } from '@/utils/whatsappHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: { Authorization: `Bearer ${token}` }
  };
};

export function CatalogModal({ lead, onClose }) {
  const [catalogItems, setCatalogItems] = React.useState([]);
  const [brands, setBrands] = React.useState([]);
  const [selectedBrand, setSelectedBrand] = React.useState('');
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [step, setStep] = React.useState('list'); // 'list' or 'detail'
  const [selectedDetail, setSelectedDetail] = React.useState(null);
  const [relatedVariants, setRelatedVariants] = React.useState([]);


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.round(amount || 0));
  };

  const getImageUrl = (image) => {
    if (!image?.storage_path) return null;
    const token = localStorage.getItem('token');
    return `${BACKEND_URL}/api/catalog/image/${image.storage_path}?auth=${token}`;
  };

  React.useEffect(() => {
    fetchCatalog();
  }, [selectedBrand]);

  const fetchCatalog = async () => {
    try {
      setLoading(true);
      const params = selectedBrand ? { brand: selectedBrand } : {};
      const response = await axios.get(`${BACKEND_URL}/api/catalog`, {
        ...getAuthHeader(),
        params
      });
      setCatalogItems(response.data || []);

      // Get brands
      const brandsRes = await axios.get(`${BACKEND_URL}/api/catalog/brands`, getAuthHeader());
      setBrands(brandsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch catalog');
    } finally {
      setLoading(false);
    }
  };

  const fetchItemDetail = async (item) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/catalog/${item.id}`, getAuthHeader());
      setSelectedDetail(response.data.item);
      setRelatedVariants(response.data.related_variants || []);
      setStep('detail');
    } catch (error) {
      console.error('Failed to fetch item detail');
    }
  };

  const toggleSelectItem = (item) => {
    setSelectedItems(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        return prev.filter(i => i.id !== item.id);
      }
      return [...prev, item];
    });
  };

  // Generate shareable URLs for ALL images (not just primary)
  const generateShareUrls = async (items) => {
    try {
      const imagePaths = [];
      items.forEach(item => {
        if (item.images && item.images.length > 0) {
          item.images.forEach(img => {
            if (img.storage_path) {
              imagePaths.push(img.storage_path);
            }
          });
        }
      });

      if (imagePaths.length === 0) return {};

      const response = await axios.post(
        `${BACKEND_URL}/api/catalog/generate-share-url`,
        { image_paths: imagePaths },
        getAuthHeader()
      );

      // Create a map of path to URL
      const urlMap = {};
      (response.data.share_urls || []).forEach(item => {
        urlMap[item.path] = item.url;
      });
      return urlMap;
    } catch (error) {
      console.error('Failed to generate share URLs:', error);
      return {};
    }
  };

  const handleWhatsAppShare = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one catalog item');
      return;
    }

    try {
      toast.info('Generating catalog link...');
      // Create a shareable page via backend
      const response = await axios.post(
        `${BACKEND_URL}/api/catalog/share/create-page`,
        {
          item_ids: selectedItems.map(item => item.id),
          title: `Vehicle Catalog - ${selectedItems.length} Option${selectedItems.length > 1 ? 's' : ''}`,
          message: `Hi ${lead.salutation || ''} ${lead.first_name || 'Customer'}, here are the vehicle options selected for you.`,
        },
        getAuthHeader()
      );

      const shareUrl = response.data.share_url;
      let message = `Hi ${lead.salutation || ''} ${lead.first_name || 'Customer'},\n\n`;
      message += `Here are the vehicle options for you:\n\n`;
      selectedItems.forEach((item, idx) => {
        message += `${idx + 1}. *${item.title}* - ${formatCurrency(item.current_price)}\n`;
      });
      message += `\nView full catalog with images:\n${shareUrl}`;
      message += `\n\nPlease let me know if you'd like more details on any of these options.\n\nRegards`;

      const whatsappResult = await sendWhatsApp(`91${lead.mobile}`, message);
      if (whatsappResult.status === 'failed') {
        toast.error(whatsappResult.error || 'Failed to send');
      } else {
        toast.success(whatsappResult.mode === 'api' ? 'Message sent via WhatsApp!' : 'Catalog link generated!');
      }
    } catch (error) {
      console.error('Failed to create share page:', error);
      toast.error('Failed to generate catalog link');
    }
  };

  const handleEmailShare = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one catalog item');
      return;
    }

    // Generate shareable URLs for images
    const shareUrls = await generateShareUrls(selectedItems);

    let subject = `Vehicle Catalog - ${selectedItems.length} Option${selectedItems.length > 1 ? 's' : ''}`;
    let body = `Dear ${lead.salutation || ''} ${lead.first_name || 'Customer'},\n\n`;
    body += `Please find below the vehicle options as discussed:\n\n`;

    selectedItems.forEach((item, idx) => {
      body += `${idx + 1}. ${item.title}\n`;
      body += `   ${item.description || `${item.model} - ${item.variant}`}\n`;
      if (item.color) body += `   Color: ${item.color}\n`;
      body += `   Price: ${formatCurrency(item.current_price)}`;
      if (item.old_price) {
        body += ` (Was ${formatCurrency(item.old_price)})`;
      }
      
      // Add ALL image URLs
      if (item.images && item.images.length > 0) {
        item.images.forEach((img, imgIdx) => {
          if (img.storage_path && shareUrls[img.storage_path]) {
            body += `\n   Image ${imgIdx + 1}: ${shareUrls[img.storage_path]}`;
          }
        });
      }
      body += `\n\n`;
    });

    body += `\nPlease feel free to reach out for more information or to schedule a test drive.\n\nBest regards`;

    // Open Microsoft Outlook mail dialog
    const mailtoUrl = `mailto:${lead.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  const handleSingleItemShare = async (item, type) => {
    if (type === 'whatsapp') {
      // Use the create-page endpoint for a rich WhatsApp preview
      try {
        toast.info('Generating catalog link...');
        const response = await axios.post(
          `${BACKEND_URL}/api/catalog/share/create-page`,
          {
            item_ids: [item.id],
            title: item.title,
            message: `Hi ${lead.salutation || ''} ${lead.first_name || 'Customer'}, check out this vehicle.`,
          },
          getAuthHeader()
        );

        const shareUrl = response.data.share_url;
        let message = `Hi ${lead.salutation || ''} ${lead.first_name || 'Customer'},\n\n`;
        message += `Check out this vehicle:\n\n`;
        message += `*${item.title}*\n`;
        if (item.color) message += `Color: ${item.color}\n`;
        message += `Price: ${formatCurrency(item.current_price)}`;
        if (item.old_price) message += ` (Was ${formatCurrency(item.old_price)})`;
        message += `\n\nView with images: ${shareUrl}`;
        message += `\n\nLet me know if you'd like to schedule a test drive!`;

        const whatsappResult = await sendWhatsApp(`91${lead.mobile}`, message);
        if (whatsappResult.status === 'failed') {
          toast.error(whatsappResult.error || 'Failed to send');
        } else {
          toast.success(whatsappResult.mode === 'api' ? 'Message sent via WhatsApp!' : 'Catalog link generated!');
        }
      } catch (error) {
        console.error('Failed to create share page:', error);
        toast.error('Failed to generate catalog link');
      }
    } else {
      // Email: keep existing behavior with raw image URLs
      let imageUrls = [];
      if (item.images && item.images.length > 0) {
        const shareUrls = await generateShareUrls([item]);
        item.images.forEach(img => {
          if (img.storage_path && shareUrls[img.storage_path]) {
            imageUrls.push(shareUrls[img.storage_path]);
          }
        });
      }

      let message = `Hi ${lead.salutation || ''} ${lead.first_name || 'Customer'},\n\n`;
      message += `Check out this vehicle:\n\n`;
      message += `*${item.title}*\n`;
      message += `${item.description || ''}\n`;
      if (item.color) message += `Color: ${item.color}\n`;
      message += `Price: ${formatCurrency(item.current_price)}`;
      if (item.old_price) message += ` (Was ${formatCurrency(item.old_price)})`;
      if (imageUrls.length > 0) {
        message += `\n`;
        imageUrls.forEach((url, idx) => { message += `\nImage ${idx + 1}: ${url}`; });
      }
      message += `\n\nLet me know if you'd like to schedule a test drive!`;

      const subject = `Vehicle Details - ${item.title}`;
      const mailtoUrl = `mailto:${lead.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
      window.location.href = mailtoUrl;
    }
  };

  // Group items by brand
  const groupedByBrand = catalogItems.reduce((acc, item) => {
    const brand = item.brand || 'Other';
    if (!acc[brand]) acc[brand] = [];
    acc[brand].push(item);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111b21] rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - WhatsApp Style */}
        <div className="bg-[#202c33] text-white p-4 flex items-center justify-between">
          <button 
            onClick={step === 'detail' ? () => setStep('list') : onClose} 
            className="text-[#00a884] font-medium"
          >
            {step === 'detail' ? '← Back' : 'Cancel'}
          </button>
          <h2 className="text-lg font-semibold">Catalog</h2>
          <button 
            onClick={handleWhatsAppShare}
            className="text-[#00a884] font-medium"
            disabled={selectedItems.length === 0}
          >
            Send
          </button>
        </div>

        {/* Send Catalog Header */}
        {step === 'list' && (
          <div className="px-4 py-2 border-b border-[#2a3942]">
            <p className="text-[#00a884] font-semibold">Send catalogue</p>
          </div>
        )}

        {/* Filter Bar */}
        {step === 'list' && (
          <div className="px-4 py-2 bg-[#202c33] flex items-center gap-2">
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="flex-1 bg-[#2a3942] text-white px-3 py-2 rounded-lg text-sm border-none outline-none"
            >
              <option value="">All Brands</option>
              {brands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
            {selectedItems.length > 0 && (
              <span className="px-3 py-1 bg-[#00a884] text-white rounded-full text-xs">
                {selectedItems.length} selected
              </span>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-[#111b21]">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00a884]"></div>
            </div>
          ) : step === 'list' ? (
            catalogItems.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <p>No catalog items found</p>
                <p className="text-sm mt-1">Add items in Catalog Master</p>
              </div>
            ) : (
              <div className="divide-y divide-[#2a3942]">
                {Object.entries(groupedByBrand).map(([brand, items]) => (
                  <div key={brand}>
                    {/* Brand Header */}
                    <div className="px-4 py-2 bg-[#202c33] text-gray-400 text-xs font-medium uppercase tracking-wide">
                      {brand} ({items.length})
                    </div>
                    {/* Items */}
                    {items.map(item => {
                      const isSelected = selectedItems.find(i => i.id === item.id);
                      const primaryImage = item.images?.find(img => img.is_primary) || item.images?.[0];
                      
                      return (
                        <div
                          key={item.id}
                          className="flex items-center p-3 hover:bg-[#202c33] transition-colors cursor-pointer"
                          onClick={() => fetchItemDetail(item)}
                        >
                          {/* Image */}
                          <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#2a3942] flex-shrink-0">
                            {primaryImage ? (
                              <img
                                src={getImageUrl(primaryImage)}
                                alt={item.title}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-500">
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                          </div>

                          {/* Details */}
                          <div className="flex-1 ml-3 min-w-0">
                            <h3 className="text-white font-semibold truncate">{item.title}</h3>
                            <p className="text-gray-400 text-sm truncate">{item.description || `${item.model} - ${item.variant}`}</p>
                            <div className="mt-1">
                              <span className="text-white font-medium">{formatCurrency(item.current_price)}</span>
                              {item.old_price && (
                                <span className="ml-2 text-gray-500 line-through text-sm">{formatCurrency(item.old_price)}</span>
                              )}
                            </div>
                          </div>

                          {/* Selection Circle */}
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleSelectItem(item); }}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 ${
                              isSelected ? 'bg-[#00a884] border-[#00a884]' : 'border-gray-500'
                            }`}
                          >
                            {isSelected && (
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )
          ) : (
            /* Detail View */
            selectedDetail && (
              <div>
                {/* Image Gallery */}
                <div className="relative">
                  <div className="aspect-square bg-[#202c33] overflow-hidden">
                    {selectedDetail.images?.length > 0 ? (
                      <img
                        src={getImageUrl(selectedDetail.images.find(img => img.is_primary) || selectedDetail.images[0])}
                        alt={selectedDetail.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {/* Image dots */}
                  {selectedDetail.images?.length > 1 && (
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1">
                      {selectedDetail.images.map((img, idx) => (
                        <div
                          key={img.id}
                          className={`w-2 h-2 rounded-full ${idx === 0 ? 'bg-white' : 'bg-white/50'}`}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Details Panel */}
                <div className="p-4 bg-[#111b21]">
                  <h2 className="text-xl font-bold text-white">{selectedDetail.title}</h2>
                  <div className="mt-2">
                    <span className="text-xl font-bold text-white">{formatCurrency(selectedDetail.current_price)}</span>
                    {selectedDetail.old_price && (
                      <span className="ml-2 text-gray-500 line-through">{formatCurrency(selectedDetail.old_price)}</span>
                    )}
                  </div>
                  <p className="text-gray-400 mt-2">{selectedDetail.description}</p>

                  {/* Share Buttons */}
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => handleSingleItemShare(selectedDetail, 'whatsapp')}
                      className="flex-1 py-3 px-4 bg-[#2a3942] text-white rounded-full flex items-center justify-center gap-2 hover:bg-[#3a4a52] transition-colors"
                    >
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current text-[#25D366]">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </button>
                    <button
                      onClick={() => handleSingleItemShare(selectedDetail, 'email')}
                      className="flex-1 py-3 px-4 bg-[#2a3942] text-white rounded-full flex items-center justify-center gap-2 hover:bg-[#3a4a52] transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Email
                    </button>
                  </div>

                  {/* Color Variants */}
                  {relatedVariants.length > 1 && (
                    <div className="mt-6 pt-4 border-t border-[#2a3942]">
                      <h3 className="text-sm text-gray-400 mb-3">Available Colors</h3>
                      <div className="flex flex-wrap gap-2">
                        {relatedVariants.map(variant => (
                          <button
                            key={variant.id}
                            onClick={() => {
                              setSelectedDetail(variant);
                            }}
                            className={`px-3 py-1.5 rounded-full text-sm ${
                              variant.id === selectedDetail.id
                                ? 'bg-[#00a884] text-white'
                                : 'bg-[#2a3942] text-gray-300 hover:bg-[#3a4a52]'
                            }`}
                          >
                            {variant.color || variant.variant}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Specifications */}
                  {selectedDetail.specifications && Object.keys(selectedDetail.specifications).length > 0 && (
                    <div className="mt-6 pt-4 border-t border-[#2a3942]">
                      <h3 className="text-sm text-gray-400 mb-3">Specifications</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(selectedDetail.specifications).map(([key, value]) => (
                          <div key={key} className="bg-[#2a3942] rounded-lg p-2">
                            <p className="text-xs text-gray-400 capitalize">{key.replace(/_/g, ' ')}</p>
                            <p className="text-white font-medium">{value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </div>

        {/* Bottom Action Bar - for list view */}
        {step === 'list' && selectedItems.length > 0 && (
          <div className="bg-[#202c33] p-4 border-t border-[#2a3942] flex gap-3">
            <button
              onClick={handleWhatsAppShare}
              className="flex-1 py-3 bg-[#00a884] text-white rounded-full font-medium flex items-center justify-center gap-2"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Send via WhatsApp
            </button>
            <button
              onClick={handleEmailShare}
              className="flex-1 py-3 bg-[#2a3942] text-white rounded-full font-medium flex items-center justify-center gap-2 hover:bg-[#3a4a52]"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Send via Email
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

